import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faEye,
  faDownload,
  faFile,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import {
  getProceduresServices,
  deleteProcedureService,
} from "../../../services/api";
import { ProcedureService } from "../../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "../AdminLayout";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../../utils/media";

const ProceduresList: React.FC = () => {
  const [procedures, setProcedures] = useState<ProcedureService[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { language } = useLanguage();

  useEffect(() => {
    fetchProcedures();
  }, []);

  const fetchProcedures = async () => {
    try {
      setLoading(true);
      const response = await getProceduresServices();
      setProcedures(response.data);
    } catch (error) {
      console.error("Error fetching procedures:", error);
      toast.error(getTranslation("fetchError", language));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(getTranslation("confirmDeleteProcedure", language))) {
      try {
        await deleteProcedureService(id);
        toast.success(getTranslation("deleteSuccess", language));
        fetchProcedures();
      } catch (error) {
        console.error("Error deleting procedure:", error);
        toast.error(getTranslation("deleteError", language));
      }
    }
  };

  const handleViewFile = (file: string) => {
    if (file) {
      window.open(resolveMediaUrl(file), "_blank");
    }
  };

  const handleDownloadFile = (file: string, filename: string) => {
    if (file) {
      const link = document.createElement("a");
      link.href = resolveMediaUrl(file);
      link.download = getFileName(file) || filename || "procedure-file";
      link.click();
    }
  };

  const filteredProcedures = procedures.filter(
    (p) =>
      p.titrearabe.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.titrefrancais.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark mb-4 md:mb-0">
          {getTranslation("adminProceduresTitle", language)}
        </h1>
        <Link
          to="/admin/procedures/new"
          className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
        >
          <FontAwesomeIcon icon={faPlus} className="ml-2" />
          {getTranslation("addNewProcedure", language)}
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
            placeholder={getTranslation(
              "searchProceduresPlaceholder",
              language,
            )}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">
            {getTranslation("loadingData", language)}
          </p>
        </div>
      ) : filteredProcedures.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-600">
            {getTranslation("noDataAvailable", language)}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {getTranslation("procedureTitleArabic", language)}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {getTranslation("procedureTitleFrench", language)}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {getTranslation("procedureFiles", language)}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {getTranslation("procedureActions", language)}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProcedures.map((procedure) => (
                  <tr key={procedure.id}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {procedure.titrearabe}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {procedure.textarabe?.substring(0, 100)}...
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {procedure.titrefrancais}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {procedure.textfrancais?.substring(0, 100)}...
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        {procedure.image && (
                          <button
                            onClick={() => handleViewFile(procedure.image!)}
                            className="text-mauritania-green hover:text-mauritania-green-dark"
                            title={`${getTranslation("preview", language)} الصورة`}
                          >
                            <FontAwesomeIcon icon={faImage} />
                          </button>
                        )}
                        {procedure.fichier && (
                          <button
                            onClick={() =>
                              handleDownloadFile(
                                procedure.fichier!,
                                `${procedure.titrearabe}.pdf`,
                              )
                            }
                            className="text-blue-600 hover:text-blue-800"
                            title={`${getTranslation("download", language)} الملف`}
                          >
                            <FontAwesomeIcon icon={faFile} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/admin/procedures/edit/${procedure.id}`}
                        className="text-mauritania-green hover:text-mauritania-green-dark mr-4"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      <button
                        onClick={() => handleDelete(procedure.id!)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ProceduresList;
